import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Small dialog for the "custom date" snooze option.
 *
 * Uses a native `<input type="date">` (no MatDatepicker adapter wiring) and
 * resolves to an ISO instant at 09:00 local on the chosen day. Closes with
 * `undefined` on cancel.
 */
@Component({
  selector: 'app-snooze-date-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title>{{ 'cockpit.snooze.dialog.title' | translate }}</h2>
    <mat-dialog-content>
      <p class="text-sm text-slate-500 dark:text-slate-400 mb-3">
        {{ 'cockpit.snooze.dialog.hint' | translate }}
      </p>
      <label for="snooze-date-input" class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
        {{ 'cockpit.snooze.dialog.label' | translate }}
      </label>
      <input
        id="snooze-date-input"
        type="date"
        [min]="minDate"
        [value]="value()"
        (input)="onInput($event)"
        data-testid="snooze-date-input"
        class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close type="button">
        {{ 'cockpit.snooze.dialog.cancel' | translate }}
      </button>
      <button
        mat-flat-button
        color="primary"
        type="button"
        [disabled]="!value()"
        (click)="confirm()"
        data-testid="snooze-confirm">
        <mat-icon class="!w-4 !h-4 !text-[16px] mr-1 leading-none">schedule</mat-icon>
        {{ 'cockpit.snooze.dialog.confirm' | translate }}
      </button>
    </mat-dialog-actions>
  `,
})
export class SnoozeDateDialogComponent {
  private readonly ref = inject(MatDialogRef<SnoozeDateDialogComponent, string>);

  readonly value = signal<string>('');
  /** Earliest selectable day = tomorrow (local). */
  readonly minDate = toLocalDay(addDays(new Date(), 1));

  protected onInput(event: Event): void {
    this.value.set((event.target as HTMLInputElement).value);
  }

  protected confirm(): void {
    const day = this.value();
    if (!day) return;
    // Parse as local 09:00 (no trailing Z) then emit the UTC instant.
    const at = new Date(`${day}T09:00:00`);
    if (isNaN(at.getTime())) return;
    this.ref.close(at.toISOString());
  }
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function toLocalDay(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}
